import { SignUpProductEnum } from "components/Signup/flow/SignupFlow/types";
import { Org, getOrg, Domain } from "../utils/site";

const EIGHT_WEEKS = 56;
const SIX_WEEKS = 42;
const FOUR_WEEKS = 28;

export enum ResolvedDomains {
  de = "de.lassie.co",
  co = "www.lassie.co",
  fr = "fr.lassie.co",
  uk = "uk.lassie.co",
}

export type Campaign = "puppy" | "kitten" | "multipet";

type CurrencyCode = "SEK" | "EUR" | "GBP";
type CurrencySymbol = "kr" | "€" | "£";
type CountryCode = "DE" | "FR" | "SE" | "GB";
export type Locale = "sv" | "de" | "en" | "fr";
export type DateFormat = "yyyy-MM-dd" | "dd.MM.yyyy" | "dd/MM/yyyy";
export enum SelectablePaymentMethodEnum {
  Stripe = "STRIPE",
  Trustly = "TRUSTLY",
  TrustlyMdd = "TRUSTLY_MDD",
  TrustlyMddManual = "TRUSTLY_MDD_MANUAL",
}

type SiteConfig = {
  appStoreUrl: string;
  currency: {
    code: CurrencyCode; // ISO currency code
    symbol: CurrencySymbol;
  };
  contactDetails: {
    email: string;
    meetingBookingUrl: string;
  };
  dateFormat: DateFormat;
  defaultLocale: Locale;
  campaigns?: {
    multipet?: string;
    /**
     * The puppy/kitten discount: its codes and the age cap that qualifies for
     * them, declared together so a market can't run the campaign without
     * stating its cap. `maxAgeMonths` must match that org's
     * `ageDiscount.maxAgeMonths` in the backend config. Markets that don't run
     * the campaign leave this out entirely.
     */
    youngAnimal?: {
      puppy?: string;
      kitten?: string;
      maxAgeMonths: number;
    };
  };
  domain: Domain;
  resolvedDomain: ResolvedDomains;
  locales: Array<Locale>;
  countryCode: CountryCode;
  tagManagerId: {
    production: string;
    development: string;
  };
  userCentricId: string;
  insurance: {
    startOffsetInDays: number;
    // Max days into the future a policy start date may be set. Set explicitly
    // per market (see getLastAllowedStartDate).
    maxStartOffsetInDays: number;
    minimumAgeInDays: number;
    copays: Array<number>;
    deductibles: Array<number>;
    preventiveCareAddOnLimits?: Array<number>;
    limits?: Array<number>;
    defaultLimit: number;
    defaultDeducible: number;
    defaultCopay: number;
    defaultBillingDay: 1 | 28;
  };
  insurancePackages: {
    [SignUpProductEnum.health]: {
      defaultPackage: "op" | "small" | "medium" | "large";
    };
    [SignUpProductEnum.liability]?: {
      defaultPackage: "liability";
    };
  };
  payment: {
    selectablePaymentMethods: Array<SelectablePaymentMethodEnum>;
    selectableBillingDays?: (
      | 1
      | 2
      | 3
      | 4
      | 5
      | 6
      | 7
      | 8
      | 9
      | 10
      | 11
      | 12
      | 13
      | 14
      | 15
      | 16
      | 17
      | 18
      | 19
      | 20
      | 21
      | 22
      | 23
      | 24
      | 25
      | 26
      | 27
      | 28
    )[];
  };
  authentication: "email" | "bankid";
  /**
   * Setting a program ID will activate the tracking in production
   */
  leadAllianceProgramId?: string;
  /**
   * Optional per-market override for the leadAlliance pixel host. When unset,
   * the tracking hooks fall back to the shared default instance URL. UK may
   * need a market-specific instance URL (pending confirmation from
   * leadAlliance).
   */
  leadAllianceInstanceUrl?: string;
  socialLadder?: {
    appGuid: string;
    areaGuid: string;
    shopName: string;
  };
};

export const sites: Record<Org, SiteConfig> = {
  swe: {
    appStoreUrl: "https://apps.apple.com/se/app/lassie/id1533808157",
    currency: {
      code: "SEK",
      symbol: "kr",
    },
    contactDetails: {
      email: "hej@lassie.co",
      meetingBookingUrl: "https://calendly.com/d/cnkg-zqy-945",
    },
    countryCode: "SE",
    dateFormat: "yyyy-MM-dd",
    defaultLocale: "sv",
    campaigns: {
      multipet: "FLERADJUR",
      youngAnimal: {
        puppy: "VALP50",
        kitten: "KATTUNGE50",
        maxAgeMonths: 6,
      },
    },
    domain: "lassie.co",
    resolvedDomain: ResolvedDomains.co,
    locales: ["sv", "en"],
    tagManagerId: {
      production: "GTM-TV3V99V",
      development: "GTM-55W2GKD",
    },
    userCentricId: "GYz0QbblVCjJYX",
    insurance: {
      minimumAgeInDays: SIX_WEEKS,
      startOffsetInDays: 0,
      // About six months (Ulf, 2026-10-21): the quote page's date picker and
      // its validation, and the BankID picker, which holds back a pet whose
      // policy renews later than this.
      maxStartOffsetInDays: 183,
      copays: [0.15, 0.25],
      deductibles: [0, 190000, 350000, 500000],
      defaultCopay: 0.25,
      defaultDeducible: 1900_00,
      defaultLimit: 60_000_00,
      defaultBillingDay: 28,
    },
    insurancePackages: {
      [SignUpProductEnum.health]: {
        defaultPackage: "medium",
      },
    },
    payment: {
      selectablePaymentMethods: [
        SelectablePaymentMethodEnum.TrustlyMdd,
        SelectablePaymentMethodEnum.Stripe,
      ],
      selectableBillingDays: [20, 28],
    },
    authentication: "bankid",
    socialLadder: {
      appGuid: "D6111BB8-9D45-4F84-8E72-5BFFA15CCB97",
      areaGuid: "8AC5F90B-97A1-48EA-B28D-28FE3628D001",
      shopName: "LASSIESWEDEN",
    },
  },
  ger: {
    appStoreUrl: "https://apps.apple.com/de/app/lassie/id1533808157",
    // No puppy/kitten campaign in Germany.
    campaigns: {
      multipet: "MOREPETS",
    },
    currency: {
      code: "EUR",
      symbol: "€",
    },
    contactDetails: {
      email: "kontakt@lassie.de",
      meetingBookingUrl:
        "https://calendly.com/d/ckfv-vjk-fvc?hide_gdpr_banner=1",
    },
    countryCode: "DE",
    dateFormat: "dd.MM.yyyy",
    defaultLocale: "de",
    domain: "lassie.de",
    resolvedDomain: ResolvedDomains.de,
    locales: ["de", "en"],
    tagManagerId: {
      production: "GTM-59KVKPV",
      development: "GTM-55W2GKD",
    },
    userCentricId: "jLt3bzUO3",
    insurance: {
      minimumAgeInDays: EIGHT_WEEKS,
      startOffsetInDays: 1,
      maxStartOffsetInDays: 364,
      copays: [0, 0.2, 0.4],
      deductibles: [],
      defaultCopay: 0.2,
      defaultDeducible: 0,
      defaultLimit: 1000_00,
      defaultBillingDay: 1,
    },
    insurancePackages: {
      [SignUpProductEnum.health]: {
        defaultPackage: "medium",
      },
      [SignUpProductEnum.liability]: {
        defaultPackage: "liability",
      },
    },

    payment: {
      selectablePaymentMethods: [
        SelectablePaymentMethodEnum.TrustlyMddManual,
        SelectablePaymentMethodEnum.Stripe,
      ],
      selectableBillingDays: [1, 15],
    },
    authentication: "email",
    leadAllianceProgramId: "2381",
    socialLadder: {
      appGuid: "3856E45B-9385-4F84-95A6-EF1342A9D657",
      areaGuid: "D79EBE27-A834-4B1F-9ABD-365780EDB476",
      shopName: "LASSIE",
    },
  },
  fra: {
    appStoreUrl: "https://apps.apple.com/fr/app/lassie/id1533808157",
    contactDetails: {
      email: "contact@lassie.fr",
      meetingBookingUrl: "https://calendly.com/d/cpnv-gdq-9zx",
    },
    currency: {
      code: "EUR",
      symbol: "€",
    },
    countryCode: "FR",
    dateFormat: "dd/MM/yyyy",
    defaultLocale: "fr",
    domain: "lassie.fr",
    resolvedDomain: ResolvedDomains.fr,
    locales: ["fr", "en"],
    payment: {
      selectablePaymentMethods: [
        SelectablePaymentMethodEnum.TrustlyMddManual,
        SelectablePaymentMethodEnum.Stripe,
      ],
      selectableBillingDays: [1, 5, 10],
    },
    tagManagerId: {
      production: "GTM-WGM4PMP5",
      development: "GTM-55W2GKD",
    },
    userCentricId: "_h3DiHVTcVErVH",
    insurance: {
      minimumAgeInDays: EIGHT_WEEKS,
      startOffsetInDays: 0,
      maxStartOffsetInDays: 364,
      copays: [0.4, 0.2, 0],
      deductibles: [],
      preventiveCareAddOnLimits: [100_00, 160_00, 220_00],
      limits: [1000_00, 2000_00, 3000_00],
      defaultCopay: 0.2,
      defaultDeducible: 0,
      defaultLimit: 2000_00,
      defaultBillingDay: 1,
    },
    insurancePackages: {
      [SignUpProductEnum.health]: {
        defaultPackage: "medium",
      },
    },
    campaigns: {
      multipet: "PLUSIEURSANIMAUX",
      youngAnimal: {
        puppy: "CHIOT30",
        kitten: "CHATON30",
        maxAgeMonths: 6,
      },
    },
    authentication: "email",
    leadAllianceProgramId: "2567",
    socialLadder: {
      areaGuid: "EF1B59AC-7AB5-4351-ADF7-37F283CF17C2",
      appGuid: "29EBB147-0409-415B-8A02-58EE427D0DDB",
      shopName: "LASSIEFRANCE",
    },
  },
  gbr: {
    appStoreUrl: "https://apps.apple.com/se/app/lassie/id1533808157",
    campaigns: {
      youngAnimal: {
        puppy: "PUPPY10",
        kitten: "KITTEN10",
        maxAgeMonths: 12,
      },
    },
    currency: {
      code: "GBP",
      symbol: "£",
    },
    contactDetails: {
      email: "",
      meetingBookingUrl: "",
    },
    countryCode: "GB",
    resolvedDomain: ResolvedDomains.uk,
    dateFormat: "dd/MM/yyyy",
    defaultLocale: "en",
    locales: ["en"],
    domain: "lassie.co.uk",
    payment: {
      selectablePaymentMethods: [SelectablePaymentMethodEnum.Stripe],
      selectableBillingDays: [1, 15, 28],
    },
    tagManagerId: {
      production: "GTM-MGJ2TWN6",
      development: "GTM-55W2GKD",
    },
    userCentricId: "",
    insurance: {
      minimumAgeInDays: FOUR_WEEKS,
      startOffsetInDays: 0,
      maxStartOffsetInDays: 60,
      copays: [0, 0.1, 0.25],
      deductibles: [49_00, 99_00, 149_00, 199_00, 249_00],
      limits: [4000_00, 8000_00, 12000_00, 20000_00],
      defaultCopay: 0,
      defaultDeducible: 149_00,
      defaultLimit: 8000_00,
      defaultBillingDay: 1,
    },
    insurancePackages: {
      [SignUpProductEnum.health]: {
        // Must match defaultLimit (800000 = medium, "Silver"). Backend rejects
        // any (name, limit, deductible) combo not in the plans table.
        defaultPackage: "medium",
      },
    },
    authentication: "email",
    // UK leadAlliance program ID (FR="2567", DE="2381").
    leadAllianceProgramId: "5515",
    // UK uses a separate leadAlliance instance host (uk.partner.lassie.co) vs
    // the shared eur host used by FR/DE. Depends on the uk.partner.lassie.co
    // CNAME being live + SSL-certified by leadAlliance before it resolves.
    leadAllianceInstanceUrl: "https://uk.partner.lassie.co/tl.php",
  },
} as const;

export const getSiteConfig = () => sites[getOrg()];
